import Vue from 'vue';
import VueRouter from "vue-router";
import VueBodyClass from "vue-body-class";
import PublicLayout from "../layouts/PublicLayout.vue";
import AppLayout from "../layouts/AppLayout.vue";
import ShareLayout from "../layouts/ShareLayout.vue";
import Explore from "../views/Explore.vue";
import Login from "../views/Login.vue";
import Register from "../views/Register.vue";
import ForgotPassword from "../views/ForgotPassword.vue";
import Search from "../components/search/Search.vue";
import Logout from "../views/Logout.vue";
import HomePage from "../components/home/new/HomePage.vue";
import Constants from "../services/constants.js";
// Reload_Guard (auto-reload-on-deploy). `isUpdatePending` is a pure query with
// no side effects on import, so a top-of-module import is safe and does not form
// a circular import (versionCheck.js imports nothing from main.js/router).
import { isUpdatePending } from "../services/appVersion/versionCheck.js";
import { shouldFullPageReload } from "../services/appVersion/reloadDecision.js";

export const reportPerms = ['Super Admin', 'Card Report'];
export const visitsReportPerms = ['Super Admin', 'Pinnacle Visits Report Access'];
export const vibespaceReportPerms = ['Super Admin', 'Vibespace Readonly Access', 'Vibespace Report Access'];
export const aggregatedDatasetPerms = ['Super Admin', 'Pinnacle Aggregate Datasets Access'];
export const datasetPerms = ['Super Admin', 'Pinnacle Bulk Export Access'];
export const homePerms = [...reportPerms, ...visitsReportPerms, ...datasetPerms, ...aggregatedDatasetPerms, ...vibespaceReportPerms];
export const explorePerms = ["Pinnacle Enterprise Access", "Pinnacle Mid Tier Access", "Pinnacle Free Access"];
export const fileManagerPerms = ['Super Admin', 'File Management'];
export const usageReportPerms = ['Super Admin', 'All Companies Access'];
export const AudiencePerms = ['Super Admin', 'product:audiences:access'];
export const accountManagementPerms = ['Super Admin', 'Account Management Tool'];

const externalLinks = ["/login", "/register", "/forgot-password", "/accept_invite", "/share/", "/share/:hash", "/embed/", "/embed/:hash"];
const doNotStoreLinks = [...externalLinks, "/settings", "/confirmation", "/share/:hash", "/embed/:hash", "/documentation", "/customer_support", "/logout", "/"];
const publicLinks = []; //["/api-reference"];

Vue.use(VueRouter);

function getRoutes() {
  return [
    {
      path: "/login",
      component: PublicLayout,
      children: [
        {
          path: "",
          name: "login",
          component: Login,
        },
      ],
    },
    {
      path: "/share/:hash",
      component: ShareLayout,
    },
    {
      path: "/embed/:hash",
      component: ShareLayout,
    },
    {
      path: "/accept_invite",
      name: "accept invite",
      component: Register,
      props: route => ({ email: route.query.email, token: route.query.invite_token })
    },
    {
      path: "/forgot-password",
      name: "forgot password",
      component: ForgotPassword,
    },
    {
      path: "/",
      component: AppLayout,
      // Redirects handled in AppLayout
      name: 'app-layout',
      children: [
        {
          path: "/documentation",
          component: () => import('../views/Documentation.vue'),
          name: "documentation",
          meta: {
            crumbName: "Documentation"
          }
        },
        {
          path: "/insights-api",
          component: () => import('../views/ApiReference.vue'),
          name: "docs-insights-api",
          meta: {
            crumbName: "API Reference"
          }
        },
        {
          path: "/docs/insights-api",
          component: () => import('../views/ApiReference.vue'),
          name: "docs-insights-api",
          meta: {
            crumbName: "API Reference"
          }
        },
        {
          path: "/customer-support",
          component: () => import('../views/CustomerSupport.vue'),
          name: "customer-support",
          meta: {
            crumbName: "Customer Support"
          }
        },
        {
          path: "/audiences",
          component: () => import('../views/Audiences.vue'),
          name: "audiences",
          meta: {
            crumbName: "Audiences",
            routeGuard: [...AudiencePerms]
          }
        },
        {
          path: "/super-ai",
          component: () => import('../views/SuperAI.vue'),
          name: "super-ai",
          meta: {
            crumbName: "Super AI"
          }
        },
        {
          path: "/settings",
          component: () => import('../views/UserInfo.vue'),
          name: "settings",
          meta: {
            crumbName: "Settings"
          }
        },
        {
          path: "/confirmation",
          component: () => import('../views/Confirmation.vue'),
          name: "confirmation",
          meta: {
            crumbName: "confirmation"
          }
        },
        {
          path: "/expire",
          component: () => import('../views/Expire.vue'),
          name: "expire",
          meta: {
            crumbName: "Expired"
          }
        },
        {
          path: "/home",
          name: "home",
          component: HomePage,
          redirect: "/home/feeds",
          children: [
            {
              path: "feeds",
              name: "home-feeds",
              component: () => import('../components/home/new/FeedLayout.vue'),
              children: [
                {
                  path: "datasets",
                  name: "home-datasets",
                  meta: {
                    routeGuard: [...datasetPerms, ...aggregatedDatasetPerms],
                  },
                  components: { "table": () => import('../components/home/new/DatasetsTable.vue'), "table-filters": () => import('../components/home/new/DatasetFilters.vue') }
                },
                {
                  path: "reports",
                  name: "home-reports",
                  meta: {
                    routeGuard: [...reportPerms, ...visitsReportPerms, ...vibespaceReportPerms],
                  },
                  components: { "table": () => import('../components/home/new/ReportsTable.vue'), "table-filters": () => import('../components/home/new/ReportFilters.vue') }
                },
                {
                  path: '*',
                  redirect: '/home'
                }
              ]
            },
            {
              path: "create",
              name: "home-create",
              component: () => import('../components/home/new/CreatePage.vue'),
              children: [
                {
                  path: "dataset",
                  name: "create-dataset",
                  component: () => import('../components/home/new/create/CreateDataset.vue'),
                  meta: {
                    title: "Format",
                    routeGuard: [...datasetPerms],
                    root: true,
                  },
                  children: [
                    {
                      path: "places",
                      name: "create-dataset-places",
                      component: () => import('../components/home/new/create/SelectPlaces.vue'),
                      meta: {
                        title: "Places",
                        step: 1,
                        routeGuard: [...datasetPerms],
                        allowMultiplePlaces: true,
                        headerTitle: "Choose your Places of Interest",
                        headerDescription:  `Select places from ${ Constants.company_name }'s place library; upload places using a GeoJSON, KML, or ESRI files; or draw a place in your browser.`
                      }
                    },
                    {
                      path: "timeframe",
                      name: "create-dataset-timeframe",
                      component: () => import('../components/home/new/create/SelectTimeframe.vue'),
                      meta: {
                        step: 2,
                        routeGuard: [...datasetPerms],
                        title: "Time Frame",
                        headerTitle: "Choose the Time Frame for your Datasets",
                        headerDescription: "This defines the date range used for visits to a place. Please note that the date range is set to UTC time for datasets. For most datasets we recommend 12 months. This period accounts for seasonal fluctuation and ensures significant data volume."
                      }
                    },
                    {
                      path: "insights",
                      name: "create-dataset-insights",
                      component: () => import('../components/home/new/create/SelectDatasetInsights.vue'),
                      meta: {
                        step: 3,
                        routeGuard: [...datasetPerms],
                        title: "Insights"
                      }
                    },
                    {
                      path: "details",
                      name: "create-dataset-details",
                      component: () => import('../components/home/new/create/SelectDatasetDetails.vue'),
                      meta: {
                        step: 4,
                        title: "Details",
                        routeGuard: [...datasetPerms],
                        submit: true,
                        headerTitle: "Specify Details for your Datasets",
                        headerDescription: "Please provide a name for your delivery. You may customize the values of the other settings or leave them at their defaults."
                      }
                    },
                    {
                      path: '*',
                      redirect: '/home'
                    }
                  ]
                },
                {
                  path: "report",
                  name: "create-report",
                  component: () => import('../components/home/new/create/CreateReport.vue'),
                  meta: {
                    title: "Format",
                    routeGuard: [...reportPerms],
                    root: true
                  },
                  children: [
                    {
                      path: "type",
                      name: "create-report-type",
                      component: () => import('@/components/home/new/create/SelectReportType.vue'),
                      meta: {
                        title: "Insights",
                        step: 1,
                      }
                    },
                    {
                      path: "place",
                      name: "create-report-places",
                      component: () => import('../components/home/new/create/SelectPlaces.vue'),
                      meta: {
                        title: "Place",
                        query: {
                          draw: true
                        },
                        step: 2,
                        routeGuard: [...reportPerms],
                        headerTitle: "Choose your Place of Interest",
                        headerDescription: `Select a place from ${ Constants.company_name }'s place library; upload a place using a GeoJSON, KML, or ESRI files; or draw a place in your browser. Visualization reports support only one place at a time.`
                      }
                    },
                    {
                      path: "timeframe",
                      name: "create-report-timeframe",
                      routeGuard: [...reportPerms],
                      component: () => import('../components/home/new/create/SelectTimeframe.vue'),
                      meta: {
                        title: "Time Frame",
                        step: 3,
                        routeGuard: [...reportPerms],
                        headerTitle: "Choose the Time Frame for your Report",
                        headerDescription: "This defines the date range used for visits to a place. Please note that the date range is set to local time for visualization reports. For most datasets we recommend 12 months. This period accounts for seasonal fluctuation and ensures significant data volume."
                      }
                    },
                    {
                      path: "details",
                      name: "create-report-details",
                      component: () => import('../components/home/new/create/SelectReportDetails.vue'),
                      meta: {
                        title: "Details",
                        step: 4,
                        submit: true,
                        routeGuard: [...reportPerms],
                        headerTitle: "Specify Details for your Report",
                        headerDescription: "You must provide a unique name for your report. The name you provide will appear at the top of your report and will be visible to those with whom you share it."
                      }
                    },
                    {
                      path: '*',
                      redirect: '/home'
                    }
                  ]
                },
                {
                  path: "visits-report",
                  name: "create-visits-report",
                  component: () => import('@/components/home/new/create/CreateVisitsReport.vue'),
                  meta: {
                    title: "Format",
                    routeGuard: [...visitsReportPerms],
                    root: true
                  },
                  children: [
                    {
                      path: "type",
                      name: "create-visits-report-type",
                      component: () => import('@/components/home/new/create/SelectReportType.vue'),
                      meta: {
                        title: "Insights",
                        step: 1,
                      }
                    },
                    {
                      path: "place",
                      name: "create-visits-report-place",
                      component: () => import('../components/home/new/create/SelectPlaces.vue'),
                      meta: {
                        title: "Place",
                        query: {
                          //draw: true
                        },
                        step: 2,
                        routeGuard: [...visitsReportPerms],
                        headerTitle: "Choose your Place of Interest",
                        headerDescription: `Select a place from ${ Constants.company_name }'s place library; upload a place using a GeoJSON, KML, or ESRI files; or draw a place in your browser. Visualization reports support only one place at a time.`
                      }
                    },
                    {
                      path: "timeframe",
                      name: "create-visits-report-timeframe",
                      routeGuard: [...visitsReportPerms],
                      component: () => import('../components/home/new/create/SelectTimeframe.vue'),
                      meta: {
                        title: "Time Frame",
                        step: 3,
                        routeGuard: [...visitsReportPerms],
                        headerTitle: "Choose the Time Frame for your Report",
                        headerDescription: "This defines the date range used for visits to a place. Please note that the date range is set to local time for visualization reports. For most datasets we recommend 12 months. This period accounts for seasonal fluctuation and ensures significant data volume."
                      }
                    },
                    {
                      path: "details",
                      name: "create-visits-report-details",
                      component: () => import('@/components/home/new/create/SelectVisitsReportDetails.vue'),
                      meta: {
                        title: "Details",
                        step: 4,
                        submit: true,
                        routeGuard: [...visitsReportPerms],
                        headerTitle: "Specify Details for your Estimated Daily Report",
                        headerDescription: "You must provide a unique name for your report. The name you provide will appear at the top of your report and will be visible to those with whom you share it."
                      }
                    },
                    {
                      path: '*',
                      redirect: '/home'
                    }
                  ]
                },
                {
                  path: "hotel-report",
                  name: "create-hotel-report",
                  component: () => import('@/components/home/new/create/CreateHotelReport.vue'),
                  meta: {
                    title: "Format",
                    routeGuard: [...visitsReportPerms],
                    root: true
                  },
                  children: [
                    {
                      path: "type",
                      name: "create-hotel-report-type",
                      component: () => import('@/components/home/new/create/SelectReportType.vue'),
                      meta: {
                        title: "Insights",
                        step: 1,
                      }
                    },
                    {
                      path: "place",
                      name: "create-hotel-report-places",
                      component: () => import('../components/home/new/create/SelectPlaces.vue'),
                      meta: {
                        title: "Places",
                        query: {
                          //draw: true
                        },
                        step: 2,
                        routeGuard: [...visitsReportPerms],
                        headerTitle: "Choose up to 5 Places of Interest",
                        headerDescription: `Select a place from ${ Constants.company_name }'s place library; upload a place using a GeoJSON, KML, or ESRI files; or draw a place in your browser. Visualization reports support only one place at a time.`
                      }
                    },
                    {
                      path: "review-places",
                      name: "create-hotel-report-review",
                      component: () => import('@/components/home/new/create/ReviewPlaces.vue'),
                      meta: {
                        title: "Review Places",
                        query: {
                          //draw: true
                        },
                        step: 3,
                        routeGuard: [...visitsReportPerms],
                        headerTitle: "Select Target Place",
                        headerDescription: `Select Target Place for the Report.  The other Places will be treated as Competitors.`
                      }
                    },
                    {
                      path: "timeframe",
                      name: "create-hotel-report-timeframe",
                      routeGuard: [...visitsReportPerms],
                      component: () => import('../components/home/new/create/SelectTimeframe.vue'),
                      meta: {
                        title: "Time Frame",
                        step: 4,
                        routeGuard: [...visitsReportPerms],
                        headerTitle: "Choose the Time Frame for your Report",
                        headerDescription: "This defines the date range used for visits to a place. Please note that the date range is set to local time for visualization reports. For most datasets we recommend 12 months. This period accounts for seasonal fluctuation and ensures significant data volume."
                      }
                    },
                    {
                      path: "details",
                      name: "create-hotel-report-details",
                      component: () => import('@/components/home/new/create/SelectVisitsReportDetails.vue'),
                      meta: {
                        title: "Details",
                        step: 5,
                        submit: true,
                        routeGuard: [...visitsReportPerms],
                        headerTitle: "Specify Details for your Hotel Comparison Report",
                        headerDescription: "You must provide a unique name for your report. The name you provide will appear at the top of your report and will be visible to those with whom you share it."
                      }
                    },
                    {
                      path: '*',
                      redirect: '/home'
                    }
                  ]
                },
                {
                  path: "vibespace-report",
                  name: "create-vibespace-report",
                  component: () => import('@/components/home/new/create/CreateVibespaceReport.vue'),
                  meta: {
                    title: "Format",
                    routeGuard: [...visitsReportPerms],
                    root: true
                  },
                  children: [
                    {
                      path: "type",
                      name: "create-vibespace-report-type",
                      component: () => import('@/components/home/new/create/SelectReportType.vue'),
                      meta: {
                        title: "Insights",
                        step: 1,
                      }
                    },
                    {
                      path: "place",
                      name: "create-vibespace-report-places",
                      component: () => import('../components/home/new/create/SelectPlaces.vue'),
                      meta: {
                        title: "Places",
                        query: {
                          //draw: true
                        },
                        step: 2,
                        routeGuard: [...visitsReportPerms],
                        headerTitle: "Choose your Places of Interest",
                        headerDescription: `Select places from ${ Constants.company_name }'s place library; upload places using a GeoJSON, KML, or ESRI files; or draw a place in your browser.`
                      }
                    },
                    {
                      path: "details",
                      name: "create-vibespace-report-details",
                      component: () => import('@/components/home/new/create/SelectVibespaceDetails.vue'),
                      meta: {
                        title: "Details",
                        step: 3,
                        submit: true,
                        routeGuard: [...visitsReportPerms],
                        headerTitle: "Specify Details for your Azira One Report",
                        headerDescription: "You must provide a unique name for your report. The name you provide will appear at the top of your report and will be visible to those with whom you share it."
                      }
                    },
                    {
                      path: '*',
                      redirect: '/home'
                    }
                  ]
                }
              ]
            },
            {
              path: "compare-datasets",
              routeGuard: [...datasetPerms],
              component: () => import('@/components/home/CompareDatasets.vue'),
              name: 'home-compare-datasets'
            },
            {
              path: "reports/:id",
              routeGuard: [...reportPerms],
              component: () => import('../components/partials/Insight.vue'),
              name: "home-reports-view"
            },
            {
              path: "visit-reports/:id",
              routeGuard: [...visitsReportPerms],
              component: () => import('@/layouts/VisitReportsLayout.vue'),
              name: "visit-reports-view",
            },
            {
              path: "hotel-reports/:id",
              routeGuard: [...visitsReportPerms],
              component: () => import('@/layouts/HotelReportLayout.vue'),
              name: "hotel-reports-view",
            },
            {
              path: "vibespace-admin",
              routeGuard: [...visitsReportPerms],
              component: () => import('@/layouts/VibeSpaceAdminLayout.vue'),
              name: "vibespace-admin",
            },
            {
              path: "vibespace-admin/:conversation_id",
              routeGuard: [...visitsReportPerms],
              component: () => import('@/layouts/VibeSpaceAdminLayout.vue'),
              name: "vibespace-admin-conversation",
            },
            {
              path: "vibespace-reports/:id",
              routeGuard: [...visitsReportPerms],
              component: () => import('@/layouts/VibeSpaceReportLayout.vue'),
              name: "vibespace-reports-view",
            },
            {
              path: '*',
              redirect: '/home'
            }
          ]
        },
        {
          path: "/explore",
          name: 'explore',
          component: Explore,
          redirect: '/explore/search',
          children: [
            {
              path: "search",
              component: Search,
              meta: {
                crumbName: "Search",
                routeGuard: [...explorePerms],
              },
              name: 'search'
            },
            {
              path: "details",
              component: () => import('../components/search/SearchDetails.vue'),
              name: 'search-details',
              meta: {
                crumbName: "Place",
                routeGuard: [...explorePerms],
              }
            },
            {
              path: "comparison",
              component: () => import('../components/search/ComparisonDetails.vue'),
              name: "comparison-details",
              meta: {
                crumbName: "Comparison",
                routeGuard: [...explorePerms],
              }
            },
            {
              path: '*',
              redirect: '/explore/search'
            }
          ]
        },
        {
          path: "/insights/usage-reports",
          component: () => import('../components/reports/UsageReportsLayout.vue'),
          meta: {
            routeGuard: [...usageReportPerms]
          },
          children: [
            {
              path: "summary",
              name: "insights-usage-reports-summary",
              component: () => import('../views/UsageReportsSummary.vue'),
              meta: {
                crumbName: "Usage Reports Summary",
                routeGuard: [...usageReportPerms]
              }
            },
            {
              path: "details",
              name: "insights-usage-reports-details",
              component: () => import('../views/UsageReportsDetails.vue'),
              meta: {
                crumbName: "Usage Reports Details",
                routeGuard: [...usageReportPerms]
              }
            }
          ]
        },
        {
          path: "/study/:ids",
          component: () => import('../layouts/StudyLayout.vue'),
          children: [
            {
              path: "map",
              name: "study-map",
              components: { default: () => import('../components/locations/StudyMapContainer.vue'), sidebar: () => import('../components/partials/PlacesDetailsCard.vue'), header: () => import('../components/partials/PlacesCardHeader.vue') },
              meta: {
                crumbName: "Comparison",
                routeGuard: [...explorePerms],
              }
            },
            {
              path: "map/details",
              name: "study-map-details",
              components: { default: () => import('../components/locations/StudyMapContainer.vue'), sidebar: () => import('../components/partials/MapPinDetails.vue') },
              meta: {
                crumbName: "Place",
                routeGuard: [...explorePerms],
              }
            },
            {
              path: "charts/details/:index",
              name: "study-charts-details",
              components: { default: () => import('../components/locations/Charts.vue'), sidebar: () => import('../components/partials/StudyDetailsCheckbox.vue') },
              meta: {
                crumbName: "Place",
                routeGuard: [...explorePerms],
              }
            },
            {
              path: "charts",
              name: "study-charts",
              components: { default: () => import('../components/locations/Charts.vue'), sidebar: () => import('../components/partials/PlacesDetailsCard.vue'), header: () => import('../components/partials/PlacesCardHeader.vue') },
              meta: {
                crumbName: "Comparison",
                routeGuard: [...explorePerms],
              },
              children: [
                {
                  path: "details",
                  components: { sidebar: () => import('../components/partials/PlaceDetails.vue') },
                  meta: {
                    crumbName: "Place",
                    routeGuard: [...explorePerms],
                  }
                }
              ]
            },
            {
              path: '*',
              redirect: '/explore/search'
            }
          ]
        },
        {
          path: "/preview/:ids",
          component: () => import('../layouts/PreviewLayout.vue'),
          children: [
            {
              path: "map",
              name: "preview-map",
              component: () => import('../components/locations/StudyMapContainer.vue'),
              meta: {
                crumbName: "Maps",
                routeGuard: [...explorePerms],
              },
            },
            {
              path: "charts",
              name: "preview-charts",
              component: () => import('../components/locations/Charts.vue'),
              meta: {
                crumbName: "Charts",
                routeGuard: [...explorePerms],
              }
            },
            {
              path: "map/details",
              name: "preview-map-details",
              components: { default: () => import('../components/locations/StudyMapContainer.vue'), sidebar: () => import('../components/partials/MapPinDetails.vue') },
              meta: {
                crumbName: "Details",
                routeGuard: [...explorePerms],
              }
            },
            {
              path: '*',
              redirect: '/explore/search'
            }
          ]
        },
        {
          name: 'places',
          path: "/places",
          component: () => import('../components/places/PlacesPage.vue'),
          meta: {
            crumbName: "Places",
            routeGuard: [...explorePerms],
          }
        },
        {
          name: 'files',
          path: "/files",
          component: () => import('../components/files/FilesLayout.vue'),
          children: [
            {
              path: "core-metrics",
              name: "core-metrics",
              components: { "table": () => import('../components/files/CoreMetrics.vue') }
            },
            {
              path: "file-manager",
              name: "file-manager",
              meta: {
                routeGuard: fileManagerPerms,
              },
              components: { "table": () => import('../components/files/FileManager.vue'), "table-filters": () => import('../components/files/FileManagerFilters.vue') }
            },
            {
              path: "file-manager/upload",
              name: "file-manager-upload",
              meta: {
                routeGuard: fileManagerPerms,
              },
              components: { "table": () => import('../components/files/FileManagerUpload.vue') }
            },
            {
              path: "signals-cross-visitation",
              name: "signals-cross-visitation",
              meta: {
                routeGuard: datasetPerms, // TODO Special Permission
              },
              components: { "table": () => import('../components/files/SignalsCrossVisitation.vue') }
            },
            {
              path: "s3-tools",
              name: "s3-tools",
              components: { "table": () => import('../components/files/S3Tools.vue') }
            }
          ]
        },
        {
          path: "/media",
          name: "media",
          component: () => import('../components/media/MediaLayout.vue'),
          children: [
            {
              path: "accounts",
              name: "media-accounts",
              component: () => import('../components/media/MediaAccounts.vue'),
            },
            {
              path: "report-types",
              name: "media-report-types",
              component: () => import('../components/media/MediaReportTypes.vue'),
            },
            {
              path: "reporting/run-report",
              name: "media-run-report",
              component: () => import('../components/media/MediaReportTypes.vue'),
            },
            {                
              path: 'accounts/:account_id',
              name: 'media-account-details',
              component: () => import('../components/media/MediaAccountDetails.vue'),
              meta: {
                crumbName: "Account Details",
                routeGuard: datasetPerms,
              }
            },
            {
              path: 'accounts/:account_id/advertisers/:advertiser_id',
              name: 'media-advertiser-details',
              component: () => import('../components/media/MediaAdvertiserDetails.vue'),
              meta: {
                crumbName: "Advertiser Details",
                routeGuard: datasetPerms,
              }
            },
            {
              path: 'accounts/:account_id/advertisers/:advertiser_id/campaigns/:campaign_id',
              name: 'media-campaign-details',
              component: () => import('../components/media/MediaCampaignDetails.vue'),
              meta: {
                crumbName: "Campaign Details",
                routeGuard: datasetPerms,
              }
            },
            {
              path: 'accounts/:account_id/advertisers/:advertiser_id/campaigns/:campaign_id/line-items/:line_item_id',
              name: 'media-line-item-details',
              component: () => import('../components/media/LineItemDetails.vue'),
              meta: {
                crumbName: "Line Item Details",
                routeGuard: datasetPerms,
              }
            }
          ]
        },
        {
          path: "/account-management",
          component: () => import('../components/accountManagement/AccountManagement.vue'),
          meta: {
            crumbName: "Account Management",
            routeGuard: [...accountManagementPerms]
          },
          children: [
            {
              path: "",
              name: "account-companies",
              component: () => import('../components/accountManagement/CompanyList.vue'),
              meta: {
                routeGuard: [...accountManagementPerms]
              }
            },
            {
              path: "new-company",
              name: "account-new-company",
              component: () => import('../components/accountManagement/NewCompany.vue'),
              meta: {
                routeGuard: [...accountManagementPerms]
              }
            },
            {
              path: "new-role",
              name: "account-new-role",
              component: () => import('../components/accountManagement/NewRole.vue'),
              meta: {
                routeGuard: [...accountManagementPerms]
              }
            },
            {
              path: "company/:companyId",
              name: "account-company",
              component: () => import('../components/accountManagement/CompanyDetails.vue'),
              meta: {
                routeGuard: [...accountManagementPerms]
              }
            },
            {
              path: "company/:companyId/user/:userId",
              name: "account-user",
              component: () => import('../components/accountManagement/UserDetails.vue'),
              meta: {
                routeGuard: [...accountManagementPerms]
              }
            }
          ]
        },
        {
          path: "/logout",
          name: "logout",
          component: Logout
        }
      ],
    },
    {
      path: "*",
      redirect: "/",
    },
  ]
}

export default function createRouter() {

  const routes = getRoutes();

  const router = new VueRouter({
    mode: 'history',
    routes,
  });


  const vueBodyClass = new VueBodyClass(routes);

  // Checks if a user's session is active
  // If not, it will boot them back to login
  router.beforeEach(async (to, from, next) => {
    vueBodyClass.guard(to, next);
    const accessToken = localStorage.getItem("accessToken");

    // Reload_Guard: called ONLY at the existing auth allow-points (where the
    // guard previously called a plain `next()`). Auth block/redirect branches
    // (next('/login')/next('/')/next('/forgot-password')) are left untouched so
    // auth outcomes are preserved byte-for-byte (Req 8.5). When an update is
    // pending and the destination differs from the current route, log first,
    // then do a full-page navigation to `to.fullPath` and cancel the SPA
    // transition with next(false) (Req 8.1, 8.7, 9.1). Otherwise proceed as a
    // normal SPA transition (Req 8.2, 8.6). $sessionStore/sessionStorage is not
    // touched here and survives location.assign naturally (Req 11.1).
    const allowNavigation = () => {
      if (
        shouldFullPageReload({
          authDecision: { allowed: true, isRedirect: false },
          isUpdatePending: isUpdatePending(),
          to,
          from,
        })
      ) {
        console.log(`[app-version] Reloading to pick up new version -> ${to.fullPath}`);
        window.location.assign(to.fullPath);
        return next(false);
      }
      return next();
    };

    if (accessToken) {
      if (externalLinks.includes(to.path)) {
        if (to.query?.return_url == "https://azira-pinnacle.elevio.help/sso/check") {
          console.log('sso login');
        } else {
          next("/"); // Check AppLayout.vue for the redirect logic
        }
        
        return;
      } else if (!doNotStoreLinks.includes(to.path)) {
        let hash_path = to.path.substring(0, to.path.lastIndexOf("/")) + '/:hash';
        if (!doNotStoreLinks.includes(hash_path)) {
          localStorage.setItem('last', to.path);
        }
      }
      allowNavigation();
    } else {
      if (externalLinks.includes(to.path) || publicLinks.includes(to.path)) {
        allowNavigation();
      } else if (to.path.indexOf("/share/") === 0) {
        allowNavigation();
      } else if (to.path.indexOf("/embed/") === 0) {
        allowNavigation();
      } 
      else if (to.path === "/settings") next("/forgot-password");
      else next("/login");
    }
  });

  return router;
}

window.popStateDetected = false;
window.addEventListener('popstate', () => {
  window.popStateDetected = true;
});